/**
 * Eine Besetzung in der Staffing-Maske (#158). Die lokale Zeile „+ Person besetzen" liegt in
 * `neue-besetzung.tsx`.
 *
 * Rechte: alles Bearbeiten mit `canResolve` (KFML/Admin); „ausplanen" mit `canAusplanen`
 * (auch PL des Projekts — der sieht dann keine Statusauswahl). Eine mit Salesforce verknüpfte
 * Besetzung zeigt die Person nur an; falsche Verknüpfung → „Verknüpfung lösen".
 */
import type {
  MaskeBesetzung,
  PatchStaffingBesetzung,
  StaffingBesetzungStatus,
  User,
} from '@projektverwaltung/shared';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../lib/api.js';
import { UserPicker } from '../../user-picker.js';
import { formatDatumKurz, formatPtPaar, formatStunden } from '../besetzung-pt.js';
import { useConfirm } from '../confirm-dialog.js';
import { EINGABE_RAHMEN, InlineFeld } from '../inline-feld.js';
import {
  useMaske,
  useMaskeKnopf,
  useMaskeSchreiben,
  usePflicht,
  usePreisText,
} from './maske-kontext.js';
import { StatusBadge, statusFarbe, useStatusText } from './status-badge.js';

const ALLE_STATUS: StaffingBesetzungStatus[] = ['vorgeschlagen', 'float', 'erledigt', 'ausgeplant'];

/** Externe kennen kein Float (vorgeschlagen → erledigt). */
export function statusOptionen(istExtern: boolean): StaffingBesetzungStatus[] {
  return istExtern ? ALLE_STATUS.filter((s) => s !== 'float') : ALLE_STATUS;
}

const KNOPF = 'text-xs hover:underline';

function Marke({ text, farbe }: { text: string; farbe: string }) {
  return (
    <span
      className="rounded px-1 py-0.5 text-[10px]"
      style={{ color: farbe, border: `1px solid ${farbe}` }}
    >
      {text}
    </span>
  );
}

/** Personenauswahl oder Umschalter „extern" + Name — nur für unverknüpfte Besetzungen. */
function PersonFeld({
  b,
  speichern,
}: {
  b: MaskeBesetzung;
  speichern: (input: PatchStaffingBesetzung) => Promise<void>;
}) {
  const { t } = useTranslation();
  const knopf = useMaskeKnopf();
  const pflicht = usePflicht();
  const [extern, setExtern] = useState(b.user_id === null);
  // UserPicker braucht ein User-Objekt; für die Anzeige reichen Id und Name.
  const aktuell: User | null = b.user_id
    ? {
        user_id: b.user_id,
        name: b.person_name,
        email: '',
        is_admin: false,
        created_at: b.created_at,
        updated_at: b.updated_at,
      }
    : null;
  return (
    <span className="flex items-center gap-2">
      <label className="flex items-center gap-1 text-xs" style={{ color: 'var(--ink-2)' }}>
        <input type="checkbox" checked={extern} onChange={(e) => setExtern(e.target.checked)} />
        {t('v2.staffing_maske.extern', 'extern')}
      </label>
      {extern ? (
        <span className="w-44">
          <InlineFeld<string>
            art="text"
            wert={b.user_id === null ? b.extern_name : null}
            kannBearbeiten
            label={t('v2.staffing_maske.extern_name', 'Name (extern)')}
            platzhalter={t('v2.staffing_maske.extern_name', 'Name (extern)')}
            onSpeichern={(neu) => speichern({ extern_name: pflicht(neu) })}
          />
        </span>
      ) : (
        <UserPicker
          value={aktuell}
          allowClear={false}
          placeholder={t('v2.staffing_maske.person_waehlen', 'Person wählen')}
          onChange={(u) => {
            if (u && u.user_id !== b.user_id)
              knopf(() =>
                api.patchStaffingBesetzung(b.staffing_besetzung_id, { user_id: u.user_id }),
              );
          }}
        />
      )}
    </span>
  );
}

/**
 * @param griff Inhalt vor der Person (Ziehgriff im Bereich „Nicht zugeordnet").
 * @param positionsAuswahl ersetzt das Positionsfeld (dort: Auswahl „Position zuordnen…").
 */
export function BesetzungZeile({
  b,
  griff,
  positionsAuswahl,
}: {
  b: MaskeBesetzung;
  griff?: ReactNode;
  positionsAuswahl?: ReactNode;
}) {
  const { t } = useTranslation();
  const { rechte, positionOptionen } = useMaske();
  const schreiben = useMaskeSchreiben();
  const knopf = useMaskeKnopf();
  const confirmAction = useConfirm();
  const statusText = useStatusText();
  const preisText = usePreisText();
  const id = b.staffing_besetzung_id;
  const name = b.person_name;
  const verknuepft = b.sf_projektbesetzung_id !== null;
  const speichern = (input: PatchStaffingBesetzung) =>
    schreiben(() => api.patchStaffingBesetzung(id, input));
  const kontext = b.sf_kontext;
  const pt = kontext ? formatPtPaar(kontext.beauftragte_pt, kontext.verbleibende_pt) : null;

  return (
    <div
      data-testid={`besetzung-${id}`}
      className="flex flex-wrap items-center gap-x-3 gap-y-1 py-1 text-sm"
    >
      {griff}
      {rechte.canResolve && !verknuepft ? (
        <PersonFeld b={b} speichern={speichern} />
      ) : (
        <span style={{ color: 'var(--ink)' }}>{name}</span>
      )}
      {rechte.canResolve ? (
        <select
          aria-label={t('v2.staffing_maske.status_von', { name })}
          className={`rounded px-1 py-0.5 text-xs ${EINGABE_RAHMEN}`}
          style={statusFarbe(b.status)}
          value={b.status}
          onChange={(e) => {
            const status = e.target.value as StaffingBesetzungStatus;
            knopf(() => api.patchStaffingBesetzung(id, { status }));
          }}
        >
          {statusOptionen(b.ist_extern).map((s) => (
            <option key={s} value={s}>
              {statusText(s)}
            </option>
          ))}
        </select>
      ) : (
        <StatusBadge status={b.status} />
      )}
      {b.sf_aktiv === true && (
        <Marke text={t('v2.staffing_maske.marke.verknuepft')} farbe="var(--info-strong)" />
      )}
      {b.sf_aktiv === false && (
        <Marke text={t('v2.staffing_maske.marke.sf_inaktiv')} farbe="var(--danger)" />
      )}
      {b.person_nicht_in_pv && (
        <Marke text={t('v2.staffing_maske.marke.nicht_in_pv')} farbe="var(--warn)" />
      )}
      {kontext && pt && (
        <span className="flex gap-2 text-xs" style={{ color: 'var(--ink-3)' }}>
          <span title={t('v2.staffing_maske.sf.satz')}>
            {preisText(kontext.vk_stundensatz, 'stunde')}
          </span>
          {kontext.vk_nebenkostenpauschale !== null && (
            <span title={t('v2.staffing_maske.sf.nk')}>
              {preisText(kontext.vk_nebenkostenpauschale, 'tag')}
            </span>
          )}
          <span title={t('v2.staffing_maske.sf.pt')}>
            {pt.beauftragt} / {pt.verbleibend}
          </span>
          <span title={t('v2.staffing_maske.sf.gebucht')}>
            {formatStunden(kontext.gebucht_stunden)}
          </span>
          <span title={t('v2.staffing_maske.sf.letzte_buchung')}>
            {formatDatumKurz(kontext.letzte_buchung)}
          </span>
        </span>
      )}
      {positionsAuswahl}
      {rechte.canResolve && positionsAuswahl === undefined && (
        <span className="w-40">
          <InlineFeld<string>
            art="auswahl"
            wert={b.beauftragte_position_id}
            optionen={positionOptionen}
            kannBearbeiten
            label={t('v2.staffing_maske.position_von', { name })}
            platzhalter={t('v2.staffing_maske.nicht_zugeordnet', 'nicht zugeordnet')}
            onSpeichern={(neu) => speichern({ beauftragte_position_id: neu })}
          />
        </span>
      )}
      <span className="ml-auto flex items-center gap-3">
        {rechte.canResolve && verknuepft && (
          <button
            type="button"
            className={KNOPF}
            style={{ color: 'var(--primary)' }}
            onClick={() =>
              knopf(() => api.patchStaffingBesetzung(id, { sf_projektbesetzung_id: null }))
            }
          >
            {t('v2.staffing_maske.verknuepfung_loesen', 'Verknüpfung lösen')}
          </button>
        )}
        {rechte.canAusplanen && b.status !== 'ausgeplant' && (
          <button
            type="button"
            className={KNOPF}
            style={{ color: 'var(--ink-2)' }}
            aria-label={t('v2.staffing_maske.ausplanen_von', { name })}
            onClick={() => knopf(() => api.patchStaffingBesetzung(id, { status: 'ausgeplant' }))}
          >
            {t('v2.staffing_maske.ausplanen', 'ausplanen')}
          </button>
        )}
        {rechte.canResolve && (
          <button
            type="button"
            className={KNOPF}
            style={{ color: 'var(--danger)' }}
            onClick={() =>
              void confirmAction({
                title: t('v2.staffing_maske.besetzung_loeschen_titel'),
                body: t('v2.staffing_maske.besetzung_loeschen_text', { name }),
                confirmLabel: t('v2.staffing_maske.besetzung_loeschen'),
                tone: 'danger',
              }).then((ok) => {
                if (ok) knopf(() => api.deleteStaffingBesetzung(id));
              })
            }
          >
            {t('v2.staffing_maske.besetzung_loeschen', 'Besetzung löschen')}
          </button>
        )}
      </span>
    </div>
  );
}
